import * as React from 'react';
import { useImperativeHandle } from 'react';
import { Typography, ListItem, ListItemText, ListItemButton, ListItemIcon, ListSubheader, List, Divider, Paper, IconButton } from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box } from '@mui/material';
import VerticalHamburgerMenu from '../../common/controls/VerticalHamburgerMenu';
import { useSnackbar } from '../../../context/SnackbarContext';
import { useTranslation } from 'react-i18next';
import { formatUtcToLocal } from '../../../utils/formatUtcToLocal';
import AppointmentForm from '../forms/AppointmentForm';
import { userHasAccess } from '../../../utils/userHasAccess';
import { SUB_MODULES } from '../../../constants/subModulesConstants';
import { useRecommendedVisits } from '../../../hooks/appointments/useRecommendedVisits';
import CheckIcon from '@mui/icons-material/Check';
import LoadingSpinner from '../../common/controls/LoadingSpinner';

export default function RecommendedVisitsListItemsDashboard({ setListEmpty = null, setDoneLoading = null, ref, refreshUpcomingAppointments = null }) {
  const { t } = useTranslation('common');
  const { showSnackbar } = useSnackbar();

  const [selectedAppointment, setSelectedAppointment] = React.useState(null);
  const [appointmentFormOpen, setAppointmentFormOpen] = React.useState(false);
  const [appointmentFormMode, setAppointmentFormMode] = React.useState('create');
  const [selectedRecommendedVisit, setSelectedRecommendedVisit] = React.useState(null);
  const [selectedPet, setSelectedPet] = React.useState(null);

  const { recommendedVisits, loading, loadMore, searchFunction, resetParent } = useRecommendedVisits();

  const onAppointmentSaveSuccess = async (updatedRecord) => {
    refreshUpcomingAppointments?.(); // Refresh upcoming appointments
    await resetParent();  // Refresh recommended visits
    setAppointmentFormOpen(false);   // Close the form
  };

  const handleAppointmentCreate = () => {
    setSelectedAppointment(null);
    setAppointmentFormMode('create');
    setAppointmentFormOpen(true);
  };

  // Expose function to parent
  useImperativeHandle(ref, () => ({
    handleAppointmentCreate
  }));

  const handleListItemClick = (recommendedVisit, pet) => {
    setSelectedRecommendedVisit(recommendedVisit);
    setSelectedPet(pet);
    handleAppointmentCreate();
  };

  return (
    <>
      {recommendedVisits.data.length === 0 ? (
        <Divider variant="fullWidth" component="li">
          <Typography variant="caption" color="text.secondary">
            no recommendations for now
          </Typography>
        </Divider>
      ) : null}

      <Box sx={{ maxHeight: 300, overflow: "auto" }}>
        <List>
          {recommendedVisits.data.map((item) => (
            <React.Fragment key={item.pet?.id}>
              <ListSubheader sx={{ pl: 0, py: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'left' }}>
                  <Typography variant="h6" sx={{ flexGrow: 1 }}>
                    {`${item.pet?.name} (${item.pet?.owner?.first_name} ${item.pet?.owner?.last_name})`}
                  </Typography>
                </Box>
              </ListSubheader>

              {item.visits?.map((recommendedVisit) => (
                <ListItem 
                  key={"recommendedVisit" + recommendedVisit.visit_date}
                  sx={{ py: 0 }}
                  alignItems="flex-start"
                  secondaryAction={
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <VerticalHamburgerMenu
                        options={[
                          { 
                            id: 1, 
                            name: 'Set Appointment',
                            callback: () => handleListItemClick(recommendedVisit, item.pet),
                            disabled: !userHasAccess(SUB_MODULES.APPOINTMENTS, 1)
                          }
                        ]}
                      />
                    </Box>
                  }
                >
                  <ListItemButton 
                    sx={{ py: 0 }} 
                    onClick={() => {
                      if (userHasAccess(SUB_MODULES.APPOINTMENTS, 1)) {
                        handleListItemClick(recommendedVisit, item.pet);
                      }
                    }}
                  >
                    <ListItemIcon>
                      <CheckIcon color='primary' />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" color='primary' sx={{ maxWidth: '90%', m:0 }}>
                          {`${formatUtcToLocal(recommendedVisit.visit_date, 'date')}`}
                        </Typography>             
                      }
                      secondary={
                        <Typography variant="body1" sx={{ maxWidth: '90%', m:0 }}>
                          {`${recommendedVisit.recommended_visit}`}
                        </Typography>
                      }
                    />
                  </ListItemButton>
                </ListItem>
              ))}
            </React.Fragment>
          ))}
        </List>

        {/* Loading spinner */}
        {loading ? <LoadingSpinner /> : null}

        {/* Load More only shows if more pages exist */}
        {recommendedVisits?.pagination?.page < recommendedVisits?.pagination?.totalPages && (
          <Box sx={{ textAlign: 'center', mt: 2 }}>
            <IconButton 
              onClick={loadMore}
              sx={{
                width: '100%',              // fill container width
                justifyContent: 'center',   // center the icon horizontally
                color: 'primary.main',      // use theme's primary color
              }}
            >
              <KeyboardArrowDownIcon />
            </IconButton>
          </Box>
        )}
      </Box>

      <AppointmentForm
        mode={appointmentFormMode}
        appointment={selectedAppointment}
        successCallback={onAppointmentSaveSuccess}
        pet={selectedPet} 
        recommendedVisit={selectedRecommendedVisit}
        open={appointmentFormOpen}
        onClose={() => setAppointmentFormOpen(false)}
      />
    </>
  );
}